<template>
  <div :dir="isRtl != false ? 'rtl' : 'ltr'" :class="['relative w-full agent-mode-selector-dropdown-container']">
    <button
      type="button"
      class="btn w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen != false"
    >
      <div v-if="selectedModeObject !== null" class="flex items-center space-x-2 w-full">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0 ring-1 ring-base-300 dark:ring-gray-600">
          <Icon :icon="selectedModeObject.icon" class="w-4 h-4 text-base-content opacity-100" />
        </span>
        <div
          class="flex-grow overflow-hidden relative w-full"
          :class="isRtl === true ? 'text-right' : 'text-left'"
          :style="isRtl === true ? 'padding-right:2%;' : ''"
        >
          <span class="block text-sm font-medium truncate relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
            {{ selectedModeObject.displayName }}
          </span>
          <span class="block text-xs text-base-content opacity-70 truncate" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
            {{ selectedModeObject.description }}
          </span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 ml-auto" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ t('agentModeSelector.selectAgentMode') }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen === true"
        class="absolute z-10 mt-1 w-full bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 agent-mode-options-list"
        ref="dropdownListRef"
      >
        <div class="py-1 space-y-1">
          <div
            v-for="mode in availableModes"
            :key="mode.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{
              'bg-primary text-primary-content': mode.id === validSelectedModeId && isModeEnabled(mode) === true,
              'hover:bg-base-200 dark:hover:bg-gray-700': mode.id !== validSelectedModeId && isModeEnabled(mode) === true,
              'opacity-50 cursor-not-allowed': isModeEnabled(mode) !== true
            }"
            @click="isModeEnabled(mode) === true ? selectModeAndClose(mode.id) : null"
            :title="isModeEnabled(mode) !== true ? 'Available on enterprise only' : ''"
            :aria-disabled="isModeEnabled(mode) !== true"
          >
            <div class="p-3">
              <div class="flex items-start space-x-3">
                <div class="flex-shrink-0">
                  <span
                    class="inline-flex justify-center items-center w-8 h-8 rounded-lg"
                    :class="mode.id === validSelectedModeId && isModeEnabled(mode) === true ? 'bg-primary-content/20 ring-1 ring-primary-content/30 shadow-sm' : 'bg-base-300 dark:bg-gray-600'"
                  >
                    <Icon :icon="mode.icon" class="w-5 h-5 opacity-100" :class="isModeEnabled(mode) !== true ? 'text-base-content opacity-50' : (mode.id === validSelectedModeId ? 'text-primary-content' : 'text-base-content')" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
                  <h3 class="text-sm font-semibold leading-tight" :class="[isModeEnabled(mode) !== true ? 'text-base-content opacity-60' : (mode.id === validSelectedModeId ? 'text-primary-content' : ''), isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ mode.displayName }}</h3>
                  <p class="text-xs mt-0.5" :class="[isModeEnabled(mode) !== true ? 'text-base-content opacity-50' : (mode.id === validSelectedModeId ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'), isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">
                    {{ mode.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';

const props = defineProps({
  selectedAgentMode: {
    type: String,
    default: 'Agentic'
  },
  allowedModes: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:selectedAgentMode', 'mode-change-requested']);

const isOpen = ref(false);
const dropdownListRef = ref(null);
const { t, currentLanguage } = useI18n();
const isRtl = computed(() => ['he','ar'].includes(currentLanguage.value) !=false ? true : false);
const isStandardSubscription = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
  return subscription === 'standard';
});

const allModes = ref([
  {
    id: 'Agentic',
    displayName: t('agentModeSelector.modes.agentic.displayName'),
    description: t('agentModeSelector.modes.agentic.description'),
    icon: 'lucide:bot'
  },
  {
    id: 'Fast',
    displayName: t('agentModeSelector.modes.fast.displayName'),
    description: t('agentModeSelector.modes.fast.description'),
    icon: 'lucide:zap'
  },
  {
    id: 'Smartest',
    displayName: t('agentModeSelector.modes.smartest.displayName'),
    description: t('agentModeSelector.modes.smartest.description'),
    icon: 'lucide:brain'
  },
  {
    id: 'SuperFast',
    displayName: t('agentModeSelector.modes.superFast.displayName', 'Super Fast'),
    description: t('agentModeSelector.modes.superFast.description', 'Direct responses without agent actions'),
    icon: 'lucide:rocket'
  },
  {
    id: 'Flow',
    displayName: t('agentModeSelector.modes.flow.displayName', 'Agent Flow'),
    description: t('agentModeSelector.modes.flow.description', 'Visual workflow based agent logic.'),
    icon: 'lucide:git-branch'
  }
]);

const availableModes = computed(() => {
  const allowed = Array.isArray(props.allowedModes) === true ? props.allowedModes.filter(mode => typeof mode === 'string' && mode.trim().length > 0) : [];
  if (allowed.length === 0) {
    return allModes.value;
  }
  return allModes.value.filter(mode => allowed.includes(mode.id) === true);
});

function normalizeModeId(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const lowered = raw.toLowerCase();
  if (raw === 'Agentic' || lowered === 'agentic' || lowered === 'conversational' || lowered === 'standard') {
    return 'Agentic';
  }
  if (raw === 'Fast' || lowered === 'fast') {
    return 'Fast';
  }
  if (raw === 'SuperFast' || lowered === 'superfast' || lowered === 'super fast') {
    return 'SuperFast';
  }
  if (raw === 'Smartest' || lowered === 'smartest') {
    return 'Smartest';
  }
  if (raw === 'Flow' || lowered === 'flow') {
    return 'Flow';
  }
  if (raw === 'FlowBased' || lowered === 'flowbased' || lowered === 'flow based') {
    return 'FlowBased';
  }
  return raw;
}

const validSelectedModeId = computed(() => {
  const ids = availableModes.value.map(m => m.id);
  const normalizedModeId = normalizeModeId(props.selectedAgentMode);
  if (ids.includes(normalizedModeId) === true) {
    return normalizedModeId;
  }
  return ids.length > 0 ? ids[0] : 'Agentic';
});

const selectedModeObject = computed(() => {
  const mode = availableModes.value.find(m => m.id === validSelectedModeId.value);
  return mode !== undefined && mode !== null ? mode : null;
});

function isModeEnabled(mode) {
  if (mode === undefined || mode === null) {
    return false;
  }
  if (isStandardSubscription.value === true && mode.id === 'Smartest') {
    return false;
  }
  return true;
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectModeAndClose = (modeId) => {
  const mode = availableModes.value.find(item => item.id === modeId);
  if (isModeEnabled(mode) !== true) {
    return;
  }
  emit('update:selectedAgentMode', modeId);
  const agenticBehaviour = modeId === 'SuperFast' ? false : true;
  emit('mode-change-requested', { modeId, agenticBehaviour });
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (dropdownListRef.value !== null) {
    if (isOpen.value === true) {
      if (dropdownListRef.value.contains(event.target) === false) {
        if (event.target.closest('.agent-mode-selector-dropdown-container button[aria-haspopup="listbox"]') === null) {
          isOpen.value = false;
        }
      }
    }
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<style scoped>
.agent-mode-options-list {
  max-height: 400px;
  overflow-y: auto;
}

</style> 